import { useCallback, useState } from "react";
import {
  createManufacturingBuildPipelinePhase,
  deleteManufacturingBuildPipelinePhase,
  resequenceManufacturingBuildPipelinePhases,
  updateManufacturingBuildPipelinePhase,
  type MfgBuildPipelinePhase,
} from "@/lib/manufacturingClient";

export function useManufacturingBuildPipelinePhaseEditor(
  phases: MfgBuildPipelinePhase[],
  onChanged: () => Promise<void>
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newKey, setNewKey] = useState("");

  const run = useCallback(
    async (fn: () => Promise<{ ok: boolean; error?: string }>) => {
      setBusy(true);
      setError(null);
      const res = await fn();
      setBusy(false);
      if (!res.ok) {
        setError(res.error ?? "Request failed.");
        return false;
      }
      await onChanged();
      return true;
    },
    [onChanged]
  );

  const rename = useCallback(
    async (phaseId: string, displayName: string) => {
      await run(async () => {
        const res = await updateManufacturingBuildPipelinePhase({
          phase_id: phaseId,
          display_name: displayName,
        });
        return res.ok ? { ok: true } : { ok: false, error: res.error };
      });
    },
    [run]
  );

  const setInitial = useCallback(
    async (phaseId: string) => {
      await run(async () => {
        const res = await updateManufacturingBuildPipelinePhase({
          phase_id: phaseId,
          is_initial: true,
        });
        return res.ok ? { ok: true } : { ok: false, error: res.error };
      });
    },
    [run]
  );

  const setBuildComplete = useCallback(
    async (phaseId: string) => {
      await run(async () => {
        const res = await updateManufacturingBuildPipelinePhase({
          phase_id: phaseId,
          is_build_complete: true,
        });
        return res.ok ? { ok: true } : { ok: false, error: res.error };
      });
    },
    [run]
  );

  const setPayTrigger = useCallback(
    async (phaseId: string, is_pay_trigger: boolean) => {
      await run(async () => {
        const res = await updateManufacturingBuildPipelinePhase({
          phase_id: phaseId,
          is_pay_trigger,
        });
        return res.ok ? { ok: true } : { ok: false, error: res.error };
      });
    },
    [run]
  );

  const remove = useCallback(
    async (phaseId: string) => {
      await run(async () => {
        const res = await deleteManufacturingBuildPipelinePhase({ phase_id: phaseId });
        return res.ok ? { ok: true } : { ok: false, error: res.error };
      });
    },
    [run]
  );

  const move = useCallback(
    async (phaseId: string, direction: -1 | 1) => {
      const ordered = [...phases].sort((a, b) => a.sequence_index - b.sequence_index);
      const idx = ordered.findIndex((p) => p.id === phaseId);
      const swap = idx + direction;
      if (idx < 0 || swap < 0 || swap >= ordered.length) return;
      const next = [...ordered];
      const tmp = next[idx];
      next[idx] = next[swap];
      next[swap] = tmp;
      await run(async () => {
        const res = await resequenceManufacturingBuildPipelinePhases({
          phase_ids: next.map((p) => p.id),
        });
        return res.ok ? { ok: true } : { ok: false, error: res.error };
      });
    },
    [phases, run]
  );

  const add = useCallback(async () => {
    const display_name = newName.trim();
    if (!display_name) {
      setError("display_name is required.");
      return;
    }
    const ok = await run(async () => {
      const res = await createManufacturingBuildPipelinePhase({
        display_name,
        phase_key: newKey.trim() || undefined,
      });
      return res.ok ? { ok: true } : { ok: false, error: res.error };
    });
    if (ok) {
      setNewName("");
      setNewKey("");
    }
  }, [newKey, newName, run]);

  return {
    busy,
    error,
    newName,
    setNewName,
    newKey,
    setNewKey,
    rename,
    setInitial,
    setBuildComplete,
    setPayTrigger,
    remove,
    move,
    add,
  };
}
